import React, { useState } from "react";

const INITIAL_FORM = {
  name: "",
  coverage_type: "CYBER_INSURANCE",
  provider: "",
  policy_number: "",
  currency: "",
  annual_premium: "",
  deductible: "",
  coverage_limit: "",
  aggregate_limit: "",
  coinsurance: "1",
  effective_date: "",
  expiration_date: "",
  status: "DRAFT",
  exclusions_summary: "",
  description: "",
};


export default function CoverageForm({
  coverage = null,
  settings,
  loading,
  onClose,
  onSubmit,
}) {

  const isEditMode = Boolean(coverage?.id);

  const [form, setForm] = useState({
    ...INITIAL_FORM,
    currency:
      coverage?.currency ||
      settings?.currency ||
      "EUR",

    name: coverage?.name || "",
    coverage_type:
      coverage?.coverage_type ||
      "CYBER_INSURANCE",
    provider: coverage?.provider || "",
    policy_number:
      coverage?.policy_number || "",
    annual_premium:
      coverage?.annual_premium ?? "",
    deductible:
      coverage?.deductible ?? "",
    coverage_limit:
      coverage?.coverage_limit ?? "",
    aggregate_limit:
      coverage?.aggregate_limit ?? "",
    coinsurance:
      coverage?.coinsurance ?? "1",
    effective_date:
      coverage?.effective_date || "",
    expiration_date:
      coverage?.expiration_date || "",
    status:
      coverage?.status || "DRAFT",
    exclusions_summary:
      coverage?.exclusions_summary || "",
    description:
      coverage?.description || "",
  });


  const [submitError, setSubmitError] =
    useState(null);

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError(null);

    const result = await onSubmit({
      ...form,
      annual_premium: Number(
        form.annual_premium || 0
      ),
      deductible: Number(
        form.deductible || 0
      ),
      coverage_limit: Number(
        form.coverage_limit || 0
      ),
      aggregate_limit:
        form.aggregate_limit === ""
          ? null
          : Number(form.aggregate_limit),
      coinsurance: Number(
        form.coinsurance || 1
      ),
    });

    if (!result?.success) {
      const responseErrors =
        result?.error?.response?.data?.errors;

      setSubmitError(
        Array.isArray(responseErrors)
          ? responseErrors.join(" ")
          : "Unable to create coverage."
      );

      return;
    }

    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30">
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {isEditMode
                ? "Edit Coverage"
                : "New Coverage"}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Register an insurance or risk-transfer arrangement.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-900"
          >
            Close
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-6 space-y-6"
        >
          <section>
            <h3 className="text-sm font-semibold text-slate-900">
              General Information
            </h3>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <TextField
                label="Coverage Name"
                value={form.name}
                required
                onChange={(value) =>
                  updateField("name", value)
                }
              />

              <SelectField
                label="Coverage Type"
                value={form.coverage_type}
                options={[
                  ["CYBER_INSURANCE", "Cyber Insurance"],
                  ["CONTRACTUAL_TRANSFER", "Contractual Transfer"],
                  ["RESERVE_FUND", "Reserve Fund"],
                  ["OTHER", "Other"],
                ]}
                onChange={(value) =>
                  updateField(
                    "coverage_type",
                    value
                  )
                }
              />

              <TextField
                label="Provider"
                value={form.provider}
                onChange={(value) =>
                  updateField(
                    "provider",
                    value
                  )
                }
              />

              <TextField
                label="Policy Number"
                value={form.policy_number}
                onChange={(value) =>
                  updateField(
                    "policy_number",
                    value
                  )
                }
              />

              <TextField
                label="Currency"
                value={form.currency}
                maxLength={3}
                onChange={(value) =>
                  updateField(
                    "currency",
                    value.toUpperCase()
                  )
                }
              />

              <SelectField
                label="Status"
                value={form.status}
                options={[
                  ["DRAFT", "Draft"],
                  ["PROPOSED", "Proposed"],
                  ["ACTIVE", "Active"],
                ]}
                onChange={(value) =>
                  updateField("status", value)
                }
              />
            </div>
          </section>

          <section className="border-t border-slate-200 pt-6">
            <h3 className="text-sm font-semibold text-slate-900">
              Financial Terms
            </h3>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <NumberField
                label="Annual Premium"
                value={form.annual_premium}
                onChange={(value) =>
                  updateField(
                    "annual_premium",
                    value
                  )
                }
              />

              <NumberField
                label="Deductible"
                value={form.deductible}
                onChange={(value) =>
                  updateField(
                    "deductible",
                    value
                  )
                }
              />

              <NumberField
                label="Coverage Limit"
                value={form.coverage_limit}
                onChange={(value) =>
                  updateField(
                    "coverage_limit",
                    value
                  )
                }
              />

              <NumberField
                label="Aggregate Limit"
                value={form.aggregate_limit}
                onChange={(value) =>
                  updateField(
                    "aggregate_limit",
                    value
                  )
                }
              />

              <NumberField
                label="Coinsurance"
                value={form.coinsurance}
                min="0"
                max="1"
                step="0.1"
                onChange={(value) =>
                  updateField(
                    "coinsurance",
                    value
                  )
                }
              />
            </div>
          </section>

          <section className="border-t border-slate-200 pt-6">
            <h3 className="text-sm font-semibold text-slate-900">
              Coverage Period
            </h3>

            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              <DateField
                label="Effective Date"
                value={form.effective_date}
                onChange={(value) =>
                  updateField(
                    "effective_date",
                    value
                  )
                }
              />

              <DateField
                label="Expiration Date"
                value={form.expiration_date}
                onChange={(value) =>
                  updateField(
                    "expiration_date",
                    value
                  )
                }
              />
            </div>
          </section>

          <section className="border-t border-slate-200 pt-6">
            <TextAreaField
              label="Description"
              value={form.description}
              onChange={(value) =>
                updateField(
                  "description",
                  value
                )
              }
            />

            <div className="mt-4">
              <TextAreaField
                label="Exclusions Summary"
                value={
                  form.exclusions_summary
                }
                onChange={(value) =>
                  updateField(
                    "exclusions_summary",
                    value
                  )
                }
              />
            </div>
          </section>

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-200 pt-5">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading
                ? "Saving..."
                : isEditMode
                ? "Save Changes"
                : "Create Coverage"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  required = false,
  maxLength,
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">
        {label}
      </span>

      <input
        type="text"
        value={value}
        required={required}
        maxLength={maxLength}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min = "0",
  max,
  step = "10000",
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">
        {label}
      </span>

      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
    </label>
  );
}

function DateField({
  label,
  value,
  onChange,
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">
        {label}
      </span>

      <input
        type="date"
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">
        {label}
      </span>

      <select
        value={value}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      >
        {options.map(([optionValue, labelText]) => (
          <option
            key={optionValue}
            value={optionValue}
          >
            {labelText}
          </option>
        ))}
      </select>
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-500">
        {label}
      </span>

      <textarea
        value={value}
        rows={3}
        onChange={(event) =>
          onChange(event.target.value)
        }
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
    </label>
  );
}